import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Modal from 'component/Modal/GenericModal'
import Button from 'component/Button'
import Form from 'component/utils/Form'
import HierarchySelect from 'component/hierarchy/HierarchySelectNew'
import Select from 'component/Select/Select'
import Plus from 'images/icon-plus.svg'
import Glyph from 'component/utils/Glyph'
import { DEPARTMENTS } from 'constants/config'

const commonConfig = {
  renderButton: true,
  showHeader: true,
  showFooter: true,
  confirmType: '',
  center: false,
}

export const MODAL_TYPE = {
  EDIT: {
    ...commonConfig,
    size: 'lg',
    title: 'datasheetManagement.edit.format.and.angles',
  },
  DELETE: {
    size: 'sm',
    center: true,
    renderButton: false,
    showHeader: false,
    showFooter: false,
    confirmType: 'danger',
    title: 'commonReport.delete',
  },
  CREATE: {
    ...commonConfig,
    size: 'md',
    title: 'datasheetManagement.formatRegisterNew',
  },
}

const FIELDS = ['sectionId', 'subSectionId', 'typeId', 'subTypeId']

const getInitialPhotoOptions = (mi) => {
  if (mi.datasheetFormat && mi.datasheetFormat.length) {
    return mi.datasheetFormat.map((df) => ({
      format: df.idDatasheetFormat,
      angle: df.angleDatasheetFormat || '',
    }))
  }
  return [{ format: '', angle: '' }]
}

const getInitialFormatOptions = (dfl, mi, defaultValue) => {
  if (mi.datasheetFormat && mi.datasheetFormat.length) {
    const modalItemFormats = mi.datasheetFormat.map(
      (df) => df.idDatasheetFormat
    )
    const formatList = dfl.filter(
      (df) => modalItemFormats.indexOf(df.idDatasheetFormat) === -1
    )
    return formatList.map((df) => ({
      value: df.idDatasheetFormat,
      label: df.nmDatasheetFormat,
    }))
  }
  return defaultValue
}

const DatasheetManagementModal = (props) => {
  const {
    closeModal,
    datasheetFormatList,
    handleDelete,
    handleSave,
    modalItem,
    modalType,
    setValueToField,
    translate,
    values,
    user,
    sections
  } = props
  const [photoOptions, setPhotoOptions] = useState(
    getInitialPhotoOptions(modalItem)
  )
  const initialValues = Array.from(photoOptions)
  const angleOptions = [1, 2, 3, 4, 5, 6, 7].map((number) => ({
    value: number,
    label: translate('datasheetManagement.angle').concat(
      ` ${number.toString()}`
    ),
  }))
  const DEFAULT_FORMAT_OPTIONS = datasheetFormatList.map((df) => ({
    value: df.idDatasheetFormat,
    label: df.nmDatasheetFormat,
  }))
  const [formatOptions, setFormatOptions] = useState(
    getInitialFormatOptions(
      datasheetFormatList,
      modalItem,
      DEFAULT_FORMAT_OPTIONS
    )
  )

  const hasRequiredFieldEmpty = () => {
    let validator = false
    if (modalType === MODAL_TYPE.CREATE) {
      validator =
        !values.sectionId ||
        (values.sectionId && values.sectionId.length === 0) ||
        validator
    }
    validator =
      photoOptions.some(
        (photoOption) =>
          photoOption.format.length === 0 || photoOption.angle.length === 0
      ) || validator
    return validator
  }

  const handleChange = (field, value) => {
    const fields = ['sectionId', 'subSectionId', 'typeId', 'subTypeId']

    if (field === 'idDatasheetFormatList' || field === 'angleDatasheetFormat') {
      setValueToField(field, value)
    } else {
      setValueToField(field, value.value)
      fields
        .slice(fields.indexOf(field) + 1)
        .forEach((f) => setValueToField(f, ''))
    }
  }

  const getName = (item, level) => {
    let section

    switch (level) {
      case 2:
        section = (sections[level] || []).find(
          (section) => section.sectionId === item.sectionId
        )
        break
      case 3:
        section = (sections[level] || []).find(
          (section) =>
            section.sectionId === item.sectionId &&
            section.subSectionId === item.subSectionId
        )
        break
      case 4:
        section = (sections[level] || []).find(
          (section) =>
            section.sectionId === item.sectionId &&
            section.subSectionId === item.subSectionId &&
            section.typeId === item.typeId
        )
        break
      case 5:
        section = (sections[level] || []).find(
          (section) =>
            section.sectionId === item.sectionId &&
            section.subSectionId === item.subSectionId &&
            section.typeId === item.typeId &&
            section.subTypeId === item.subTypeId
        )
        break
      default:
        break
    }

    if (section) {
      return `${section[DEPARTMENTS[level]]} - ${section.description}`
    }
    return null
  }

  const handleFormatAngleSelectChange = (field, value, i) => {
    if (field === 'format') {
      const prev = DEFAULT_FORMAT_OPTIONS.find(
        (defaultFormatOption) =>
          defaultFormatOption.value === photoOptions[i].format
      )
      const formatOptionsCopy = Array.from(formatOptions)
      const format = formatOptions.find(
        (formatOption) => formatOption.value === value
      )
      const formatIndex = formatOptions.indexOf(format)
      formatOptionsCopy.splice(formatIndex, 1)
      if (prev) setFormatOptions([prev, ...formatOptionsCopy])
      else setFormatOptions(formatOptionsCopy)
    }
    const photoOptionsCopy = Array.from(photoOptions)
    photoOptionsCopy[i] = { ...photoOptions[i], [field]: value }
    setPhotoOptions(photoOptionsCopy)
  }

  const addPhotoOption = () => {
    const photooptionsCopy = Array.from(photoOptions)
    photooptionsCopy.push({ format: '', angle: '' })
    setPhotoOptions(photooptionsCopy)
  }

  const removePhotoOption = (i) => {
    const photoOptionsCopy = Array.from(photoOptions)
    photoOptionsCopy.splice(i, 1)
    setPhotoOptions(photoOptionsCopy)
    const option = DEFAULT_FORMAT_OPTIONS.find(
      (defaultFormatOption) =>
        defaultFormatOption.value === photoOptions[i].format
    )
    const formatOptionsCopy = Array.from(formatOptions)
    setFormatOptions([option, ...formatOptionsCopy])
  }

  const renderContent = () => {
    const renderPhotoOptions = (deleteButton) => (
      <div>
        {photoOptions.map((option, i) => (
          <div key={option.format.concat(i.toString())} className='flex'>
            <Select
              label={translate('datasheetManagement.format').concat(
                ` ${(i + 1).toString()}`
              )}
              options={formatOptions}
              placeholder={translate('select.format')}
              translate={translate}
              onChange={({ value }) =>
                handleFormatAngleSelectChange('format', value, i)
              }
              value={DEFAULT_FORMAT_OPTIONS.find(
                (formatOption) => formatOption.value === option.format
              )}
              required
              fill
              containerClass='margin-right-small'
            />
            <Select
              label={translate('datasheetManagement.format.angle').concat(
                ` ${(i + 1).toString()}`
              )}
              options={angleOptions}
              placeholder={translate('select.angle')}
              translate={translate}
              onChange={({ value }) =>
                handleFormatAngleSelectChange('angle', value, i)
              }
              value={angleOptions.find(
                (angleOption) => angleOption.value === option.angle
              )}
              required
              fill
              disabled={photoOptions[i].format.length === 0}
              containerClass='margin-left-small'
            />
            {deleteButton && (
              <div className='table-icon-center padding flex align-items-center'>
                <Glyph
                  disabled={
                    photoOptions.length <= 1 ||
                    photoOptions[i].format.length === 0
                  }
                  type='trash-can'
                  color='danger'
                  customClass='cursor-pointer'
                  onClick={() => removePhotoOption(i)}
                />
              </div>
            )}
          </div>
        ))}
        <span
          className={`add-field no-margin ${
            ( formatOptions.length === 0 || hasRequiredFieldEmpty()) &&
            'add-field-disabled'
          }`}
          onClick={
              !hasRequiredFieldEmpty() && formatOptions.length > 0
              ? () => addPhotoOption()
              : () => {}
          }
        >
          <img src={Plus} />
          {translate('datasheetManagement.format.angle.add')}
        </span>
      </div>
    )

    switch (modalType) {
      case MODAL_TYPE.CREATE:
        return (
          <div className='w-100 margin-top padding-right'>
            <div>
              <HierarchySelect
                fill
                label={translate('section')}
                id='sectionId'
                field='sectionId'
                name='sectionId'
                onChange={(values) => handleChange('sectionId', values)}
                placeholder='select.section'
                query={{ level: 2 }}
                translate={translate}
                user={user}
                value={values.sectionId}
                required
                clearButton
                concatId
              />
            </div>
            <div>
              <HierarchySelect
                fill
                label={translate('subSection')}
                id='subSectionId'
                field='subSectionId'
                name='subSectionId'
                onChange={(values) => handleChange('subSectionId', values)}
                placeholder='select.subsection'
                query={{
                  level: 3,
                  sectionId: values.sectionId,
                }}
                translate={translate}
                user={user}
                value={values.subSectionId}
                required
                disabled={!values.sectionId}
                clearButton
                concatId
              />
            </div>
            <div>
              <HierarchySelect
                fill
                label={translate('type')}
                id='typeId'
                field='typeId'
                name='typeId'
                onChange={(values) => handleChange('typeId', values)}
                placeholder='select.type'
                query={{
                  level: 4,
                  sectionId: values.sectionId,
                  subSectionId: values.subSectionId,
                }}
                translate={translate}
                user={user}
                value={values.typeId}
                required
                disabled={!values.subSectionId}
                clearButton
                concatId
              />
            </div>
            <div>
              <HierarchySelect
                fill
                label={translate('subType')}
                id='subTypeId'
                field='subTypeId'
                name='subTypeId'
                onChange={(values) => handleChange('subTypeId', values)}
                placeholder='select.subtype'
                query={{
                  level: 5,
                  sectionId: values.sectionId,
                  subSectionId: values.subSectionId,
                  typeId: values.typeId,
                }}
                translate={translate}
                user={user}
                value={values.subTypeId}
                required
                disabled={!values.typeId}
                clearButton
                concatId
              />
            </div>
            <h5 className='margin-bottom-double'>
              <strong>
                {translate(
                  'datasheetManagement.formatRegisterNew.formats.and.angles'
                )}
              </strong>
            </h5>
            {renderPhotoOptions()}
          </div>
        )
      case MODAL_TYPE.EDIT:
        return (
          <div className='w-100 padding-right'>{renderPhotoOptions(true)}</div>
        )
      case MODAL_TYPE.DELETE:
        const sectionIdNm = getName(modalItem, 2)
        const subSectionIdNm = getName(modalItem, 3)
        const typeIdNm = getName(modalItem, 4)
        const subTypeIdNm = getName(modalItem, 5)
        const formatNames = modalItem.datasheetFormat

        return (
          <div className='remove-modal'>
            <i className='glyph glyph-trash-can color-danger margin-bottom' />
            <p className='title no-margin'>{translate(modalType.title)}</p>
            <h4 className='margin-top margin-bottom'>
              {translate('datasheetManagement.new.delete.message')}
            </h4>
            {sectionIdNm && (
              <span className='margin-bottom-small show'>
                <span>
                  <strong>{`${translate('section')}: `}</strong>
                  {`${sectionIdNm}`}
                </span>
              </span>
            )}
            {subSectionIdNm && (
              <span className='margin-bottom-small show'>
                <span>
                  <strong>{`${translate('subSection')}: `}</strong>
                  {`${subSectionIdNm}`}
                </span>
              </span>
            )}
            {typeIdNm && (
              <span className='margin-bottom-small show'>
                <span>
                  <strong>{`${translate('type')}: `}</strong>
                  {`${typeIdNm}`}
                </span>
              </span>
            )}
            {subTypeIdNm && (
              <span className='margin-bottom-small show'>
                <span>
                  <strong>{`${translate('subType')}: `}</strong>
                  {`${subTypeIdNm}`}
                </span>
              </span>
            )}
            {formatNames.length && (
              <span className='margin-bottom-small show'>
                <strong>{`${translate(
                  formatNames.length > 1
                    ? 'datasheetManagement.formats'
                    : 'datasheetManagement.format'
                )}: `}</strong>
                {formatNames.map(
                  (format, i) =>
                    `${format.nmDatasheetFormat}${
                      i < formatNames.length - 1 ? ', ' : ''
                    }`
                )}
              </span>
            )}
            <div className='button-row'>
              <Button
                className='btn custom-modal-close-button'
                text={translate('close_button_label')}
                onClick={closeModal}
              />
              <Button
                className='btn btn-danger'
                text={translate('button.yes.remove.subtype')}
                onClick={() => beforeHandleDelete(modalItem)}
              />
            </div>
          </div>
        )
      default:
        return <div />
    }
  }

  const beforeHandleSave = () => {
    const { sectionId, subSectionId, typeId, subTypeId } = values

    handleSave({
      ...{
        sectionId: typeof sectionId === 'string' ? null : sectionId,
        subSectionId: typeof subSectionId === 'string' ? null : subSectionId,
        typeId: typeof typeId === 'string' ? null : typeId,
        subTypeId: typeof subTypeId === 'string' ? null : subTypeId,
      },
      datasheetFormatList: photoOptions,
    })
  }

  const beforeHandleDelete = ({ idDatasheetManagement }) => {
    handleDelete({
      idDatasheetManagement,
      datasheetFormatList: photoOptions,
    })
  }

  const beforeHandleEdit = () => {
    handleSave({
      sectionId: modalItem.sectionId,
      subSectionId: modalItem.subSectionId,
      typeId: modalItem.typeId,
      subTypeId: modalItem.subTypeId,
      idDatasheetManagement: modalItem.idDatasheetManagement,
      datasheetFormatList: photoOptions,
    })
  }

  return (
    <Modal
      title={translate(modalType.title)}
      size={modalType.size}
      isOpen
      onCloseModal={closeModal}
      disabledBgClickClosing
      buttonClass='new-login-error-modal-button'
      onConfirm={
        (modalType === MODAL_TYPE.CREATE && beforeHandleSave) ||
        (modalType === MODAL_TYPE.EDIT && beforeHandleEdit)
      }
      cancelButtonText={translate('close_button_label')}
      showHeader={modalType.showHeader}
      showFooter={modalType.showFooter}
      center={modalType.center}
      confirmType={modalType.confirmType}
      confirmButtonText={translate('save_button_label')}
      isConfirmButtonDisabled={
        hasRequiredFieldEmpty() || initialValues === photoOptions
      }
    >
      {renderContent()}
    </Modal>
  )
}

DatasheetManagementModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  datasheetFormatList: PropTypes.array.isRequired,
  fields: PropTypes.object.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  modalItem: PropTypes.object.isRequired,
  modalType: PropTypes.object.isRequired,
  setValueToField: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  values: PropTypes.object,
  sections: PropTypes.object,
  user: PropTypes.object.isRequired,
}

export default Form({ fields: FIELDS })(DatasheetManagementModal)

export { DatasheetManagementModal }
